// Asset labels (brief §5.5, installation guide §7): QR = <publicUrl>/a/<company_code>/<asset_code> (brief fix #2)
// with the code printed beside it. Made in the browser — no secrets are involved:
//   PDF  A4 sheet, 24 per page (3 × 8, 70 × 37 mm — Avery L7159-style) or a 50 × 25 mm roll (one per page)
//   ZPL  Zebra ZD421 at 203 dpi, 50 × 25 mm
//   CSV  for the engraver of stainless plates (60 × 30 mm)
// Zone signs (CR_20260914): QR = <publicUrl>/z/<company_code>/<zone_id>, as an A4 sign, two A5 signs per A4 page,
// or the asset label sizes above for shelf edges.
// The QR is drawn as vector squares, so it stays sharp at any printer resolution.
import { zoneLabelUrl } from '@fleetspoke/core/zones';
import { PDFDocument, type PDFFont, type PDFPage, rgb, StandardFonts } from 'pdf-lib';
import QRCode from 'qrcode';

export { zoneLabelUrl } from '@fleetspoke/core/zones';

export interface LabelItem {
  assetCode: string;
  companyCode: string;
  companyName: string;
  description?: string | null;
}

export interface ZoneLabelItem {
  zoneId: string;
  zoneName: string;
  siteName: string;
  companyCode: string;
  companyName: string;
}

export type Layout = 'a4_24' | 'roll_50x25';
export type ZoneLayout = 'sign_a4' | 'sign_a5x2' | Layout;

export function labelUrl(publicUrl: string, companyCode: string, assetCode: string): string {
  return `${publicUrl.replace(/\/+$/, '')}/a/${encodeURIComponent(companyCode)}/${encodeURIComponent(assetCode)}`;
}

/** QR modules (true = dark), error correction M (survives some scratches and dirt). */
export function qrMatrix(text: string): boolean[][] {
  const qr = QRCode.create(text, { errorCorrectionLevel: 'M' });
  const n = qr.modules.size;
  return Array.from({ length: n }, (_, y) =>
    Array.from({ length: n }, (_, x) => Boolean(qr.modules.get(y, x))),
  );
}

const MM = 72 / 25.4;
// Characters a standard PDF font can print (printable ASCII + Latin-1 supplement), and printable ASCII for ZPL.
const LATIN1_UNPRINTABLE = new RegExp('[^\\x20-\\x7e\\xa0-\\xff]', 'g');
const ASCII_UNPRINTABLE = new RegExp('[^\\x20-\\x7e]', 'g');
const ZPL_CONTROL = new RegExp('[\\^~\\\\]', 'g');

/** Text a label would print with gaps: standard PDF fonts only have Latin-1 (e.g. a zone named in Chinese). */
export function unprintable(text: string): boolean {
  return new RegExp(LATIN1_UNPRINTABLE.source).test(text);
}

/** Standard PDF fonts only encode Latin-1: anything else (e.g. a Chinese description) is dropped. */
function latin(
  text: string | null | undefined,
  font: PDFFont,
  size: number,
  maxWidth: number,
): string {
  let s = (text ?? '').replace(LATIN1_UNPRINTABLE, '').trim();
  while (s && font.widthOfTextAtSize(s, size) > maxWidth) s = s.slice(0, -2) + '…';
  return s.replace(/…/g, '...').slice(0, 120);
}

/** The largest size (down to `min`) at which the printable text fits `maxWidth`. */
function fitSize(text: string, font: PDFFont, max: number, min: number, maxWidth: number): number {
  const s = text.replace(LATIN1_UNPRINTABLE, '').trim();
  let size = max;
  while (size > min && font.widthOfTextAtSize(s, size) > maxWidth) size -= 1;
  return size;
}

interface Cell {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Fonts {
  bold: PDFFont;
  regular: PDFFont;
}

/** What one label shows: the QR and up to three lines of text. */
interface Content {
  url: string;
  title: string;
  line1: string;
  line2?: string | null;
}

const assetContent = (item: LabelItem, publicUrl: string): Content => ({
  url: labelUrl(publicUrl, item.companyCode, item.assetCode),
  title: item.assetCode,
  line1: item.companyName,
  line2: item.description,
});

const zoneContent = (item: ZoneLabelItem, publicUrl: string): Content => ({
  url: zoneLabelUrl(publicUrl, item.companyCode, item.zoneId),
  title: item.zoneName,
  line1: item.siteName,
  line2: item.companyName,
});

/** A QR square of `size` points with its bottom-left corner at (x0, y0). */
function drawQr(page: PDFPage, url: string, x0: number, y0: number, size: number) {
  const matrix = qrMatrix(url);
  const quiet = 2; // modules of white border inside the square
  const mod = size / (matrix.length + 2 * quiet);
  matrix.forEach((row, r) => {
    // merge horizontal runs of dark modules into one rectangle
    let c = 0;
    while (c < row.length) {
      if (!row[c]) {
        c++;
        continue;
      }
      const start = c;
      while (c < row.length && row[c]) c++;
      page.drawRectangle({
        x: x0 + (quiet + start) * mod,
        y: y0 + size - (quiet + r + 1) * mod,
        width: (c - start) * mod,
        height: mod,
        color: rgb(0, 0, 0),
      });
    }
  });
}

/** Small label: QR on the left, title + two lines on the right. */
function draw(page: PDFPage, cell: Cell, content: Content, fonts: Fonts) {
  const pad = Math.min(cell.h * 0.1, 3 * MM);
  const qrSize = cell.h - 2 * pad;
  const x0 = cell.x + pad;
  drawQr(page, content.url, x0, cell.y + pad, qrSize);
  const tx = x0 + qrSize + pad;
  const tw = cell.x + cell.w - tx - pad;
  const codeSize = Math.min(cell.h * 0.3, 16);
  page.drawText(latin(content.title, fonts.bold, codeSize, tw), {
    x: tx,
    y: cell.y + cell.h / 2 + codeSize * 0.15,
    size: codeSize,
    font: fonts.bold,
  });
  const small = Math.max(5, codeSize * 0.45);
  page.drawText(latin(content.line1, fonts.regular, small, tw), {
    x: tx,
    y: cell.y + cell.h / 2 - small * 1.6,
    size: small,
    font: fonts.regular,
  });
  const line2 = latin(content.line2, fonts.regular, small, tw);
  if (line2) {
    page.drawText(line2, {
      x: tx,
      y: cell.y + cell.h / 2 - small * 3,
      size: small,
      font: fonts.regular,
    });
  }
}

/**
 * Zone sign: a big QR and the zone name. Portrait cells (A4) stack them, landscape cells (A5 halves) put the QR on
 * the left. `hint` is the "scan with the app" line, in a Latin-script language (standard fonts).
 */
function drawSign(
  page: PDFPage,
  cell: Cell,
  content: Content,
  hint: string,
  qrMm: number,
  fonts: Fonts,
) {
  const m = 12 * MM;
  const text = (
    s: string | null | undefined,
    font: PDFFont,
    size: number,
    x: number,
    y: number,
    w: number,
  ) => {
    const line = latin(s, font, size, w);
    if (line) page.drawText(line, { x, y, size, font, color: rgb(0, 0, 0) });
  };
  if (cell.w > cell.h) {
    const qs = Math.min(qrMm * MM, cell.h - 2 * m);
    const qx = cell.x + m;
    drawQr(page, content.url, qx, cell.y + (cell.h - qs) / 2, qs);
    const tx = qx + qs + 10 * MM;
    const tw = cell.x + cell.w - tx - m;
    const size = fitSize(content.title, fonts.bold, 54, 18, tw);
    text(content.title, fonts.bold, size, tx, cell.y + cell.h / 2 + 8 * MM, tw);
    text(content.line1, fonts.regular, 16, tx, cell.y + cell.h / 2 - 4 * MM, tw);
    text(content.line2, fonts.regular, 11, tx, cell.y + cell.h / 2 - 11 * MM, tw);
    text(hint, fonts.regular, 9, tx, cell.y + m, tw);
  } else {
    const qs = Math.min(qrMm * MM, cell.w - 2 * m);
    const qTop = cell.y + cell.h - m;
    drawQr(page, content.url, cell.x + (cell.w - qs) / 2, qTop - qs, qs);
    const tw = cell.w - 2 * m;
    const centred = (s: string | null | undefined, font: PDFFont, size: number, y: number) => {
      const line = latin(s, font, size, tw);
      if (line)
        page.drawText(line, {
          x: cell.x + (cell.w - font.widthOfTextAtSize(line, size)) / 2,
          y,
          size,
          font,
          color: rgb(0, 0, 0),
        });
    };
    const size = fitSize(content.title, fonts.bold, 72, 24, tw);
    const nameY = qTop - qs - 12 * MM - size * 0.7;
    centred(content.title, fonts.bold, size, nameY);
    centred(content.line1, fonts.regular, 22, nameY - 14 * MM);
    centred(content.line2, fonts.regular, 14, nameY - 23 * MM);
    centred(hint, fonts.regular, 12, cell.y + m);
  }
}

async function newDoc(): Promise<{ doc: PDFDocument; fonts: Fonts }> {
  const doc = await PDFDocument.create();
  doc.setTitle('Fleetspoke Assets labels');
  doc.setProducer('Fleetspoke Assets');
  const fonts = {
    bold: await doc.embedFont(StandardFonts.HelveticaBold),
    regular: await doc.embedFont(StandardFonts.Helvetica),
  };
  return { doc, fonts };
}

/** Small labels: one per 50 × 25 mm roll page, or 24 per A4 sheet. */
function smallLabels(doc: PDFDocument, contents: Content[], layout: Layout, fonts: Fonts) {
  if (layout === 'roll_50x25') {
    for (const content of contents) {
      const page = doc.addPage([50 * MM, 25 * MM]);
      draw(page, { x: 0, y: 0, w: 50 * MM, h: 25 * MM }, content, fonts);
    }
    return;
  }
  const cols = 3;
  const rows = 8;
  const w = 70 * MM;
  const h = 37 * MM;
  const top = 297 * MM - (297 * MM - rows * h) / 2;
  for (let i = 0; i < contents.length; i += cols * rows) {
    const page = doc.addPage([210 * MM, 297 * MM]);
    const chunk = contents.slice(i, i + cols * rows);
    for (const [k, content] of chunk.entries()) {
      const col = k % cols;
      const row = Math.floor(k / cols);
      draw(page, { x: col * w, y: top - (row + 1) * h, w, h }, content, fonts);
    }
  }
}

export async function labelsPdf(
  items: LabelItem[],
  layout: Layout,
  publicUrl: string,
): Promise<Uint8Array> {
  const { doc, fonts } = await newDoc();
  smallLabels(
    doc,
    items.map((i) => assetContent(i, publicUrl)),
    layout,
    fonts,
  );
  return doc.save();
}

/** Zone signs (A4 / two A5 per A4) or zone labels in the small layouts. */
export async function zoneLabelsPdf(
  items: ZoneLabelItem[],
  layout: ZoneLayout,
  publicUrl: string,
  hint: string,
): Promise<Uint8Array> {
  const { doc, fonts } = await newDoc();
  const contents = items.map((i) => zoneContent(i, publicUrl));
  const A4: [number, number] = [210 * MM, 297 * MM];
  if (layout === 'sign_a4') {
    for (const content of contents) {
      drawSign(doc.addPage(A4), { x: 0, y: 0, w: A4[0], h: A4[1] }, content, hint, 120, fonts);
    }
  } else if (layout === 'sign_a5x2') {
    const half = A4[1] / 2;
    for (let i = 0; i < contents.length; i += 2) {
      const page = doc.addPage(A4);
      // a light cut line between the two halves
      page.drawLine({
        start: { x: 0, y: half },
        end: { x: A4[0], y: half },
        thickness: 0.5,
        color: rgb(0.75, 0.75, 0.75),
        dashArray: [4, 4],
      });
      drawSign(page, { x: 0, y: half, w: A4[0], h: half }, contents[i]!, hint, 80, fonts);
      const second = contents[i + 1];
      if (second) drawSign(page, { x: 0, y: 0, w: A4[0], h: half }, second, hint, 80, fonts);
    }
  } else {
    smallLabels(doc, contents, layout, fonts);
  }
  return doc.save();
}

const zplText = (s: string, max = 40) =>
  s.replace(ZPL_CONTROL, ' ').replace(ASCII_UNPRINTABLE, '').slice(0, max);

/** Zebra ZPL, 50 × 25 mm at 203 dpi (8 dots/mm): QR on the left, title + first line on the right. */
function zpl(contents: Content[]): string {
  return contents
    .map((c) =>
      [
        '^XA',
        '^CI28',
        '^PW400',
        '^LL200',
        `^FO12,12^BQN,2,4^FDMA,${zplText(c.url, 200)}^FS`,
        `^FO190,60^A0N,40,40^FD${zplText(c.title)}^FS`,
        `^FO190,112^A0N,20,20^FD${zplText(c.line1)}^FS`,
        '^XZ',
      ].join('\n'),
    )
    .join('\n');
}

export function labelsZpl(items: LabelItem[], publicUrl: string): string {
  return zpl(items.map((i) => assetContent(i, publicUrl)));
}

export function zoneLabelsZpl(items: ZoneLabelItem[], publicUrl: string): string {
  return zpl(items.map((i) => zoneContent(i, publicUrl)));
}

const csvCell = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
const csv = (rows: string[][]) => rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';

/** For the engraver: one row per plate. */
export function labelsCsv(items: LabelItem[], publicUrl: string): string {
  return csv([
    ['asset_code', 'company_code', 'qr_url', 'description'],
    ...items.map((item) => [
      item.assetCode,
      item.companyCode,
      labelUrl(publicUrl, item.companyCode, item.assetCode),
      item.description ?? '',
    ]),
  ]);
}

/** Zone plates for an open yard (paper fades outdoors). */
export function zoneLabelsCsv(items: ZoneLabelItem[], publicUrl: string): string {
  return csv([
    ['zone_name', 'site', 'company_code', 'qr_url'],
    ...items.map((item) => [
      item.zoneName,
      item.siteName,
      item.companyCode,
      zoneLabelUrl(publicUrl, item.companyCode, item.zoneId),
    ]),
  ]);
}

/** Offer a file to the browser. */
export function download(name: string, data: Uint8Array | string, type: string) {
  const blob = new Blob([typeof data === 'string' ? data : new Uint8Array(data)], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
